import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { string, arrayOf, func } from 'prop-types';
import {
  isEmpty, forEach, chain, map,
} from 'lodash';
import useCompanyMembersNames from '../../hooks/use-company-members';
import { Checkbox, Dropdown, Message } from './styles';

const UserDropdownCheckbox = (props) => {
  const {
    placeholder,
    value,
    onChange,
    height,
    width,
    companyId,
    userId,
  } = props;

  const [open, setOpen] = useState(false);

  const {
    users = [],
    loading,
    error,
  } = useCompanyMembersNames(companyId, userId);

  const userSelection = useMemo(() => {
    if (isEmpty(users)) return [];

    const checkedUsers = { selectAll: true, users: {} };

    forEach(users, (user) => {
      const isSelected = value.includes(user.id);
      checkedUsers.users[user.id] = { id: user.id, checked: isSelected };
      if (!isSelected) {
        checkedUsers.selectAll = false;
      }
    });
    return checkedUsers;
  }, [users, value]);

  const handleSelected = useCallback((checked, selectedUserId) => {
    if (checked) {
      const checkedUsers = [
        ...chain(userSelection.users).filter('checked').map('id').value(),
        selectedUserId,
      ];
      onChange(checkedUsers, checkedUsers.length === users.length);
    } else {
      onChange(chain(userSelection.users).filter((v) => v.id !== selectedUserId && v.checked).map('id').value(), false);
    }

    setOpen(true);
  }, [userSelection, onChange, users]);

  const handleSelectAll = useCallback((checked) => {
    const allUsers = map(userSelection.users, 'id');

    onChange(checked ? allUsers : [], checked);
    setOpen(true);
  }, [userSelection, onChange]);

  const handleClose = useCallback((event) => {
    const { target } = event || {};
    const clickedInsideItem = target?.closest?.('.item');

    if (clickedInsideItem) {
      setOpen(true);
      return;
    }

    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    if (!loading) {
      onChange(map(userSelection.users, 'id'), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, loading]);

  const text = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }

    let displayText = '';
    if (userSelection?.selectAll) {
      displayText += 'All';
    } else if (value.length) {
      displayText += value.length;
    } else {
      displayText += 'No';
    }
    displayText += ` Project Creator${value.length !== 1 ? 's' : ''}`;
    return displayText;
  }, [placeholder, userSelection, value]);

  return (
    <Dropdown
      open={open}
      onClose={handleClose}
      onOpen={() => setOpen(true)}
      text={text}
      closeOnChange={false}
      fluid
      className="selection"
      height={height}
      width={width}
      loading={loading}
      error={error}
    >
      <Dropdown.Menu>
        {error && (
          <Message
            error
            header="Error"
            content="Something went wrong"
          />
        )}

        {!error && !loading
          && (
            <>
              <Dropdown.Item>
                <Checkbox
                  checked={userSelection?.selectAll}
                  label="All Project Creators"
                  onFocus={() => setOpen(true)}
                  onChange={(e, { checked }) => handleSelectAll(checked)}
                />
              </Dropdown.Item>

              {!isEmpty(users) && map(users, (user) => {
                const { id, name } = user;
                return (
                  <Dropdown.Item key={id}>
                    <Checkbox
                      checked={userSelection?.users[id]?.checked}
                      label={id === userId ? `${name} (me)` : name}
                      onFocus={() => setOpen(true)}
                      onChange={(e, { checked }) => handleSelected(checked, id)}
                    />
                  </Dropdown.Item>
                );
              })}
            </>
          )}
      </Dropdown.Menu>

    </Dropdown>
  );
};

UserDropdownCheckbox.propTypes = {
  placeholder: string,
  value: arrayOf(string),
  onChange: func.isRequired,
  height: string,
  width: string,
  companyId: string.isRequired,
  userId: string.isRequired,
};

UserDropdownCheckbox.defaultProps = {
  value: [],
  height: undefined,
  width: undefined,
  placeholder: null,
};

export default UserDropdownCheckbox;
